import { LineSetType, PositionType } from 'data/types';

export function linesetHasPointLevels(lineset: LineSetType): boolean {
  return lineset.points.some((point) => !!point.levelUniqueId);
}

export function resolveLinesetPointLevels(
  lineset: LineSetType
): (string | undefined)[] {
  const { points } = lineset;

  if (linesetHasPointLevels(lineset)) {
    const levels: (string | undefined)[] = [];
    let previousLevel =
      points.find((point) => !!point.levelUniqueId)?.levelUniqueId ??
      lineset.condenserLevelUniqueId;
    points.forEach((point) => {
      const level = point.levelUniqueId ?? previousLevel;
      levels.push(level ?? undefined);
      previousLevel = level;
    });
    return levels;
  }

  if (lineset.isCrossFloor === true && points.length >= 2) {
    if (points.length === 2) {
      return [lineset.condenserLevelUniqueId, lineset.airHandlerLevelUniqueId];
    }
    const transitionPointIndex = points.length - 2;
    return points.map((_, index) =>
      index <= transitionPointIndex
        ? lineset.condenserLevelUniqueId
        : lineset.airHandlerLevelUniqueId
    );
  }

  return points.map(
    () => lineset.condenserLevelUniqueId ?? lineset.airHandlerLevelUniqueId
  );
}

export function materializeLinesetPointLevels(lineset: LineSetType): void {
  if (linesetHasPointLevels(lineset)) return;
  const pointLevels = resolveLinesetPointLevels(lineset);
  const { points } = lineset;
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    point.levelUniqueId = pointLevels[index];
  }
}

export function getSegmentLevel(
  pointLevels: (string | undefined)[],
  segmentIndex: number
): string | undefined {
  const startLevel = pointLevels[segmentIndex];
  const endLevel = pointLevels[segmentIndex + 1];
  return startLevel === endLevel ? startLevel : endLevel;
}

export function resolveSegmentLevelForPointInsertion(
  lineset: LineSetType,
  segmentIndex: number
): string | undefined {
  const segmentLevel = getSegmentLevel(
    resolveLinesetPointLevels(lineset),
    segmentIndex
  );
  materializeLinesetPointLevels(lineset);
  return segmentLevel;
}

export function isLevelTransitionPoint(
  pointLevels: (string | undefined)[],
  pointIndex: number
): boolean {
  if (pointIndex >= pointLevels.length - 1) return false;
  return pointLevels[pointIndex] !== pointLevels[pointIndex + 1];
}

export type CrossLevelPointInsertion = {
  position: PositionType;
  levelUniqueId: string | undefined;
};

export function buildCrossLevelPointInsertions(
  pointLevels: (string | undefined)[],
  segmentIndex: number,
  snappedPoint: PositionType,
  targetLevelUniqueId: string
): CrossLevelPointInsertion[] {
  const segmentLevelUniqueId = getSegmentLevel(pointLevels, segmentIndex);
  return [
    { position: snappedPoint, levelUniqueId: segmentLevelUniqueId },
    { position: snappedPoint, levelUniqueId: targetLevelUniqueId }
  ];
}
